package com.dualremotetest

import kotlin.math.floor

// The few things the game needs from the platform it runs on (Android/JVM or the browser), and plain-Kotlin
// stand-ins for the java.util classes it used to take from the JVM, so the same rules run everywhere.

/** A monotonic clock in nanoseconds (System.nanoTime on the JVM, performance.now in the browser). */
expect fun nanoTime(): Long

/** Wall-clock milliseconds since 1970. */
expect fun currentTimeMillis(): Long

/** String.format on the JVM; in the browser, the subset the game uses: %s %d %x %f %c %%, with flags `-` `0`, width and precision. */
expect fun String.format(vararg args: Any?): String

/** "HH:mm:ss.SSS" in local time, for log lines. */
expect fun clockTime(wallMs: Long): String

/** The current local date and time with its UTC offset, "yyyy-MM-dd HH:mm:ss Z", for report headers. */
expect fun reportTimestamp(): String

/** Math.round for doubles: halves round up (kotlin.math.round would round them to even). */
fun roundHalfUp(x: Double): Long = floor(x + 0.5).toLong()

/**
 * java.util.Random's generator, bit for bit (the 48-bit linear congruential generator its documentation specifies),
 * so a seed replays the same match on Android and in the browser, and the seeded tests keep their outcomes.
 */
class JavaRandom(seed: Long = nanoTime()) {
    private var state = 0L

    init {
        setSeed(seed)
    }

    fun setSeed(seed: Long) {
        state = (seed xor MULTIPLIER) and MASK
    }

    private fun next(bits: Int): Int {
        state = (state * MULTIPLIER + ADDEND) and MASK
        return (state ushr (48 - bits)).toInt()
    }

    fun nextInt(): Int = next(32)

    /** A value in 0 until [bound], exactly as java.util.Random.nextInt(bound). */
    fun nextInt(bound: Int): Int {
        require(bound > 0) { "bound must be positive" }
        if (bound and -bound == bound) return ((bound.toLong() * next(31).toLong()) shr 31).toInt()
        var bits: Int
        var v: Int
        do {
            bits = next(31)
            v = bits % bound
        } while (bits - v + (bound - 1) < 0)
        return v
    }

    private companion object {
        const val MULTIPLIER = 0x5DEECE66DL
        const val ADDEND = 0xBL
        const val MASK = (1L shl 48) - 1
    }
}

/** A min-heap of longs, standing in for java.util.PriorityQueue<Long>. */
class LongHeap {
    private var a = LongArray(64)
    private var size = 0

    fun isNotEmpty() = size > 0

    fun add(v: Long) {
        if (size == a.size) a = a.copyOf(size * 2)
        var i = size++
        while (i > 0) {
            val parent = (i - 1) / 2
            if (a[parent] <= v) break
            a[i] = a[parent]
            i = parent
        }
        a[i] = v
    }

    /** Removes and returns the smallest value, or null when empty. */
    fun poll(): Long? {
        if (size == 0) return null
        val top = a[0]
        val last = a[--size]
        var i = 0
        while (true) {
            var c = 2 * i + 1
            if (c >= size) break
            if (c + 1 < size && a[c + 1] < a[c]) c++
            if (a[c] >= last) break
            a[i] = a[c]
            i = c
        }
        if (size > 0) a[i] = last
        return top
    }
}
